import { ManagedSource } from '../types';
import { NormalizedSourceListQuery } from './sourceListQuery';

// COM-647 Task 3: pure fixture-shaping logic for the `getManagedSources` mock
// (mockSocket.ts), extracted for the same reason `articlesFeedMock.ts` was -
// see that file's own doc comment for the precedent - so the filter/search/
// sort/paginate rules the Sources Settings table's e2e suite implicitly
// relies on can be unit-tested directly.
//
// Mirrors `DatabaseService#getManagedSources` (backend/src/services/dbService.ts)
// row-for-row:
//   - `search` matches name OR url, case-insensitive substring (the brief:
//     "a source may have no name" - an empty name still matches on url);
//   - `monitored`: `null` = no filter (both), `true`/`false` filter to
//     exactly one;
//   - `dateFrom`/`dateTo` filter on `createdAt`, inclusive of the whole
//     calendar day at both ends, in UTC (same shape as the feed's own date
//     filter);
//   - `sortBy: 'name'` compares the lowercased name, `sortBy: 'createdAt'`
//     compares the raw ISO string; `id` (not `url` - this table has no
//     per-row url-uniqueness the way the feed's dedup does) breaks ties;
//   - `total` counts the filtered set BEFORE pagination.

// `YYYY-MM-DD` -> that day's midnight UTC, comparable to `createdAt`'s ISO
// string. Mirrors `articlesFeedMock.ts`'s own `startOfUtcDay`/
// `startOfNextUtcDay` exactly - not shared, since duplicating two 3-line
// helpers is cheaper than introducing a shared module for them.
function startOfUtcDay(dateStr: string): string {
  return `${dateStr}T00:00:00.000Z`;
}

function startOfNextUtcDay(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString();
}

export function filterManagedSourcesBySearch(rows: ManagedSource[], search: string | null): ManagedSource[] {
  if (!search) {
    return rows;
  }

  const needle = search.toLowerCase();
  return rows.filter(
    (row) => row.name.toLowerCase().includes(needle) || row.url.toLowerCase().includes(needle)
  );
}

export function filterManagedSourcesByMonitored(rows: ManagedSource[], monitored: boolean | null): ManagedSource[] {
  if (monitored === null) {
    return rows;
  }
  return rows.filter((row) => row.isMonitored === monitored);
}

export function filterManagedSourcesByDateRange(
  rows: ManagedSource[],
  dateFrom: string | null,
  dateTo: string | null
): ManagedSource[] {
  if (dateFrom === null && dateTo === null) {
    return rows;
  }

  const lowerBound = dateFrom !== null ? startOfUtcDay(dateFrom) : null;
  const upperBound = dateTo !== null ? startOfNextUtcDay(dateTo) : null;

  return rows.filter((row) => {
    if (lowerBound !== null && row.createdAt < lowerBound) {
      return false;
    }
    if (upperBound !== null && row.createdAt >= upperBound) {
      return false;
    }
    return true;
  });
}

// Plain byte-order comparison, not `localeCompare` - mirrors
// `articlesFeedMock.ts`'s own `compareStrings` (matching the real backend's
// `C`-collation Postgres comparison rather than depending on
// `localeCompare`'s locale-aware behaviour lining up by fixture luck).
function compareStrings(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function sortManagedSources(
  rows: ManagedSource[],
  sortBy: 'name' | 'createdAt',
  sortDir: 'asc' | 'desc'
): ManagedSource[] {
  const dirMultiplier = sortDir === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const primary =
      sortBy === 'name'
        ? compareStrings(a.name.toLowerCase(), b.name.toLowerCase())
        : compareStrings(a.createdAt, b.createdAt);

    if (primary !== 0) {
      return primary * dirMultiplier;
    }

    // `id ASC` tiebreak, always ascending regardless of the requested
    // direction - mirrors `DatabaseService#getManagedSources`'s
    // `ORDER BY ... , id ASC`.
    return compareStrings(a.id, b.id);
  });
}

export function paginateManagedSources(rows: ManagedSource[], limit: number, offset: number): ManagedSource[] {
  return rows.slice(offset, offset + limit);
}

// The full pipeline the mock's `getManagedSources` case runs: search ->
// monitored -> date range -> sort -> paginate, with `total` counting the
// filtered set (matching the backend's separate `COUNT(*)` query, which
// shares the same WHERE clause). `query` must already be normalized
// (`normalizeSourceListQuery` from `./sourceListQuery`) - this function
// trusts `limit`/`offset` to be in range and does not re-clamp them itself.
export function buildManagedSourcesPage(
  rows: ManagedSource[],
  query: NormalizedSourceListQuery
): { sources: ManagedSource[]; total: number; offset: number } {
  const bySearch = filterManagedSourcesBySearch(rows, query.search);
  const byMonitored = filterManagedSourcesByMonitored(bySearch, query.monitored);
  const filtered = filterManagedSourcesByDateRange(byMonitored, query.dateFrom, query.dateTo);
  const sorted = sortManagedSources(filtered, query.sortBy, query.sortDir);
  const page = paginateManagedSources(sorted, query.limit, query.offset);

  return {
    sources: page,
    total: filtered.length,
    offset: query.offset,
  };
}
